import React, { createContext, useContext, useState, useEffect } from "react";
import { auth, db } from "../config/firebase";
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signOut,
    createUserWithEmailAndPassword
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    const adminDoc = await getDoc(doc(db, "admins", user.uid));
                    if (adminDoc.exists()) {
                        setUserRole("admin");
                        setUserData(adminDoc.data());
                    } else {
                        const partnerDoc = await getDoc(doc(db, "partners", user.uid));
                        if (partnerDoc.exists()) {
                            setUserRole("partner");
                            setUserData(partnerDoc.data());
                        } else {
                            const managerDoc = await getDoc(doc(db, "managers", user.uid));
                            if (managerDoc.exists()) {
                                setUserRole("manager");
                                setUserData(managerDoc.data());
                            } else {
                                const employeeDoc = await getDoc(doc(db, "employees", user.uid));
                                if (employeeDoc.exists()) {
                                    const data = employeeDoc.data();
                                    setUserRole(data.role || 'employee');
                                    setUserData(data);
                                } else {
                                    setUserRole("unknown");
                                    setUserData(null);
                                }
                            }
                        }
                    }
                } catch (error) {
                    console.error("Error fetching user role:", error);
                    setUserRole("error");
                }
                setCurrentUser(user);
            } else {
                setCurrentUser(null);
                setUserRole(null);
                setUserData(null);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const login = async (email, password) => {
        return signInWithEmailAndPassword(auth, email, password);
    };

    const signup = async (email, password) => {
        return createUserWithEmailAndPassword(auth, email, password);
    };

    const logout = () => {
        setUserRole(null);
        setUserData(null);
        return signOut(auth);
    };

    const value = {
        currentUser,
        userRole,
        userData,
        login,
        signup,
        logout,
        loading
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
